// src/modules/ambassadors/utils/ambassadorUtils.ts - Утилиты для амбассадоров

import { AmbassadorProfile } from '@/types/models/ambassador';

/**
 * Форматирование промокода в UPPERCASE
 */
export const formatPromoCode = (code: string): string => {
  return code.toUpperCase().trim();
};

/**
 * Валидация промокода (только A-Z, 0-9 и подчеркивание)
 */
export const validatePromoCode = (code: string): {
  isValid: boolean;
  error?: string;
} => {
  if (!code || code.length === 0) {
    return { isValid: false, error: 'Промокод не может быть пустым' };
  }

  if (code.length < 3) {
    return { isValid: false, error: 'Промокод должен содержать минимум 3 символа' };
  }

  if (code.length > 50) {
    return { isValid: false, error: 'Промокод не может быть длиннее 50 символов' };
  }

  const promoCodeRegex = /^[A-Z0-9_]+$/;
  if (!promoCodeRegex.test(code.toUpperCase())) {
    return {
      isValid: false,
      error: 'Промокод может содержать только латинские буквы, цифры и подчеркивание',
    };
  }

  return { isValid: true };
};

/**
 * Получение текста статуса амбассадора
 */
export const getAmbassadorStatusText = (isActive: boolean): string => {
  return isActive ? 'Активен' : 'Неактивен';
};

/**
 * Получение цвета для статуса амбассадора
 */
export const getAmbassadorStatusColor = (isActive: boolean): string => {
  return isActive ? 'green' : 'red';
};

/**
 * Форматирование баланса бонусов
 */
export const formatBonusBalance = (balance: number): string => {
  return `${balance.toLocaleString('ru-RU')} ₸`;
};

/**
 * Форматирование процента комиссии
 */
export const formatCommissionRate = (rate: number): string => {
  return `${rate}%`;
};

/**
 * Получение уровня квалификации текстом
 */
export const getQualificationLevelText = (level: number | null): string => {
  if (level === null) return 'Не квалифицирован';
  
  const levels: Record<number, string> = {
    1: 'Уровень 1',
    2: 'Уровень 2',
    3: 'Уровень 3',
  };
  
  return levels[level] || 'Неизвестный уровень';
};

/**
 * Получение цвета для уровня квалификации
 */
export const getQualificationLevelColor = (level: number | null): string => {
  if (level === null) return 'default';
  
  const colors: Record<number, string> = {
    1: 'blue',
    2: 'purple',
    3: 'gold',
  };
  
  return colors[level] || 'default';
};

/**
 * Получение текста типа транзакции
 */
export const getTransactionTypeText = (type: string): string => {
  const types: Record<string, string> = {
    commission: 'Комиссия',
    payment: 'Оплата',
    adjustment: 'Корректировка',
    refund: 'Возврат',
  };
  
  return types[type] || type;
};

/**
 * Получение цвета для типа транзакции
 */
export const getTransactionTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    commission: 'green',
    payment: 'red',
    adjustment: 'blue',
    refund: 'orange',
  };
  
  return colors[type] || 'default';
};

/**
 * Форматирование суммы транзакции с знаком
 */
export const formatTransactionAmount = (amount: number, type: string): string => {
  const sign = ['commission', 'adjustment'].includes(type) && amount > 0 ? '+' : '';
  return `${sign}${amount.toLocaleString('ru-RU')} ₸`;
};

/**
 * Расчет процента выполнения для достижения следующего уровня
 */
export const calculateLevelProgress = (
  currentClients: number,
  currentOrders: number,
  nextLevelClients: number,
  nextLevelOrders: number
): {
  clientsProgress: number;
  ordersProgress: number;
  overallProgress: number;
} => {
  const clientsProgress = Math.min((currentClients / nextLevelClients) * 100, 100);
  const ordersProgress = Math.min((currentOrders / nextLevelOrders) * 100, 100);
  const overallProgress = (clientsProgress + ordersProgress) / 2;
  
  return {
    clientsProgress: Math.round(clientsProgress),
    ordersProgress: Math.round(ordersProgress),
    overallProgress: Math.round(overallProgress),
  };
};

/**
 * Генерация случайного промокода
 */
export const generateRandomPromoCode = (prefix?: string): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = prefix ? prefix.toUpperCase() + '_' : '';
  
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return code;
};

/**
 * Проверка, может ли амбассадор быть удален
 */
export const canDeleteAmbassador = (ambassador: AmbassadorProfile): {
  canDelete: boolean;
  reason?: string;
} => {
  if (ambassador.total_new_clients > 0) {
    return {
      canDelete: false,
      reason: 'У амбассадора есть привлеченные клиенты',
    };
  }
  
  if (ambassador.ambassador_bonus_balance > 0) {
    return {
      canDelete: false,
      reason: 'У амбассадора есть баланс бонусов',
    };
  }
  
  return { canDelete: true };
};

/**
 * Форматирование даты активации
 */
export const formatActivationDate = (date: string | null): string => {
  if (!date) return 'Не активирован';
  
  return new Intl.DateTimeFormat('ru-RU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
};

/**
 * Получение текста для пустого состояния статистики
 */
export const getEmptyStatsText = (type: 'clients' | 'sales' | 'bonuses'): string => {
  const texts: Record<string, string> = {
    clients: 'Еще не привлечено клиентов',
    sales: 'Еще нет продаж',
    bonuses: 'Еще нет заработанных бонусов',
  };
  
  return texts[type] || 'Нет данных';
};

/**
 * Расчет среднего чека привлеченных клиентов
 */
export const calculateAverageOrderValue = (totalSales: number, totalOrders: number): number => {
  if (totalOrders === 0) return 0;
  return Math.round(totalSales / totalOrders);
};

/**
 * Форматирование телефона для отображения
 */
export const formatPhoneDisplay = (phone: string): string => {
  // Удаляем все нечисловые символы
  const cleaned = phone.replace(/\D/g, '');
  
  // Форматируем в +7 (XXX) XXX-XX-XX
  if (cleaned.length === 11 && cleaned.startsWith('7')) {
    return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  }
  
  return phone;
};

/**
 * Копирование промокода в буфер обмена
 */
export const copyPromoCodeToClipboard = async (promoCode: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(promoCode);
    return true;
  } catch (err) {
    console.error('Failed to copy promo code:', err);
    return false;
  }
};

/**
 * Получение текста для реферальной ссылки
 */
export const getReferralLink = (promoCode: string, baseUrl?: string): string => {
  const base = baseUrl || window.location.origin;
  return `${base}/?promo=${promoCode}`;
};

/**
 * Определение цвета для карточки статистики
 */
export const getStatCardColor = (type: 'clients' | 'sales' | 'bonuses' | 'active'): string => {
  const colors: Record<string, string> = {
    clients: '#1890ff',
    sales: '#52c41a',
    bonuses: '#faad14',
    active: '#13c2c2',
  };
  
  return colors[type] || '#1890ff';
};

/**
 * Форматирование числа с сокращениями (K, M)
 */
export const formatNumberShort = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};